import { PathPlannerOperationMode } from "@ptx-autonomous/libs/api";
import type {
	PlannedPathCollection,
	SimplePoint,
} from "@ptx-autonomous/libs/api-models";
import type { LineString, MultiPolygon, Polygon, Position } from "geojson";

export type PassOrder =
	| "SkipOrder"
	| "ConsecutiveOrder"
	| "MultiSkipOrder"
	| "CustomOrder";

export type HeadlandSizeSearch = "SearchOn" | "SearchOff";
export type HeadlandType = "SymmetricHeadland" | "AsymmetricHeadland";
export type HeadingSetupMode = "manual" | "fieldEdge" | "abLine";
export type PlanningBoundary = "inner" | "outer";

export const DEFAULT_HEADLAND_TYPE: HeadlandType = "SymmetricHeadland";
export const DEFAULT_INNER_BOUNDARY_SAFETY_MARGIN_METERS = 0;
export const DEFAULT_OUTER_BOUNDARY_SAFETY_MARGIN_METERS = 1.5;
export const HEADLAND_LINKER_SPACE_METERS = 0.6;
export const TOP_HEADLAND_DIRECTION_OFFSET_DEGREES = 90;

export type ReferenceABLine = {
	ReferencePoint: SimplePoint;
	ABLineHeading: number;
};

export type GetPathOptions = {
	heading: number;
	headingSetupMode?: HeadingSetupMode;
	overlap: number;
	swathExtension: number;
	prefersConsecutiveOrder: boolean;
	targetSpeed: number;
	turningSpeed: number;
	approachSpeed: number;
	startLocation?: Position;
	referenceABLine?: ReferenceABLine;
	uncapHeadlands: boolean;
	headlandType: HeadlandType;
	planningBoundary: PlanningBoundary;
	obstacleSafetyMarginMeters?: number;
	outerBoundarySafetyMarginMeters?: number;
	sideHeadlandWidthMeters?: number;
	topHeadlandDirection?: number;
	lengthBufferMeters?: number;
	widthBufferMeters?: number;
	maxSlipPercentage?: number;
	slipTimeoutSec?: number;
};

export type ReplanPathOptions = {
	uncapHeadlands: boolean;
	allowNonConsecutiveTurns: boolean;
	optimizeStartPoint: boolean;
};

export type PathPlannerOptions = {
	heading: number;
	depth: number;
	implementWidthMeters: number;
	swathExtensionLength: number;
	turningRadiusMeters: number;
	overlap: number;
	pattern: PassOrder;
	operationMode: PathPlannerOperationMode;
	skipPass?: number;
	headlandType: HeadlandType;
	obstacleSafetyMarginMeters?: number;
	outerBoundarySafetyMarginMeters: number;
	sideHeadlandWidthMeters: number;
	topHeadlandWidthMeters: number;
	topHeadlandDirection: number;
	rearAxleCenterToFrontLengthMeters: number;
	robotLengthMeters: number;
	robotWidthMeters: number;
	tractorRearAxleToRearOfMachineMeters: number;
	lengthBufferMeters?: number;
	widthBufferMeters?: number;
};

export const DubinsSteeringStateSpace = "CC00_Dubins" as const;
export const DualCurvatureSteeringStateSpace = "CC00_Dual_Curvature" as const;
export type SteeringStateSpace =
	| typeof DubinsSteeringStateSpace
	| typeof DualCurvatureSteeringStateSpace;

export type PathPlanningRequest = {
	RowHeading: number;
	Depth: number;
	ImplementWidthMeters: number;
	SwathExtensionLength: number;
	TurningRadiusMeters: number;
	TractorTurningRadiusMeters: number;
	HitchToImplementAxle: number;
	RearAxleToHitch: number;
	RearAxleCenterToFrontLength: number;
	RobotLength: number;
	RobotWidth: number;
	TractorRearAxleToRearOfMachine: number;
	ImplementTotalWidth?: number;
	ImplementTotalLength?: number;
	LengthBuffer?: number;
	WidthBuffer?: number;
	OperationMode: PathPlannerOperationMode;
	TurnPointDistance: number;
	FieldBoundary: SimplePoint[];
	Geofence: SimplePoint[];
	Overlap: number;
	Obstacles: SimplePoint[][];
	ObstacleSafetyMargin?: number;
	OuterBoundarySafetyMargin: number;
	PassOrder: PassOrder;
	StartPoint?: SimplePoint;
	ReferenceABLine?: ReferenceABLine;
	HeadlandType: HeadlandType;
	SideHeadlandWidth: number;
	TopHeadlandWidth: number;
	TopHeadlandDirection: number;
	HeadlandSearchIncrement: number;
	HeadlandSizeSearch?: HeadlandSizeSearch;
	SteeringStateSpace: SteeringStateSpace;
	TurnFunction?: "ImplementForwardTurns";
	boundaryID?: string;
	boundaryName?: string;
	requestID?: string;
	machineID?: string;
	machineName?: string;
	userID?: string;
	accountID?: string;
};

export type LoggingInfo = {
	boundaryID: string;
	boundaryName: string;
	requestID: string;
	machineID: string;
	machineName: string;
	userID: string;
	accountID: string;
};

export type ParametricPathSegment = {
	CurvatureRate: number;
	East: number;
	InitialCurvature: number;
	InitialHeading: number;
	Length: number;
	MaxSpeedKph: number;
	North: number;
	SegmentMetadata: string;
	SegmentType: "Swath" | "Turn";
	Up: number;
};

export type ParametricPathMetadata = {
	Created: string;
	Datum: string;
	ID: string;
};

export type ParametricPath = {
	Metadata: ParametricPathMetadata;
	Origin: SimplePoint;
	Segments: ParametricPathSegment[];
	Type: string;
};

export type PathPlanningResponse = {
	Status?: "success";
	Boundary: {
		InnerFieldBoundary: SimplePoint[];
	};
	Boundaries: {
		InnerFieldBoundaries: SimplePoint[][];
	};
	MitigationData: {
		CorrectedTurns: SimplePoint[][];
		ShortenedSegments: {
			Start: SimplePoint;
			End: SimplePoint;
		}[];
	};
	Path: PlannedPathCollection[];
	ParametricPath: ParametricPath;
	Version: string;
	requestID: string;
};

export type PathPlanningErrorResponse =
	| { Status: "rejected"; ErrorMessage: string }
	| { Status: "failed"; ErrorMessage: string };

export type PreMissionMetrics = {
	fieldArea: number;
	workAreaArea: number;
	pathLength: number;
	swathLength: number;
	turnLength: number;
	swathTime: number;
	turnTime: number;
	totalTime: number;
	efficiencyRatio: number;
};

export type PlanPathResult = {
	passOrder: PassOrder;
	startOption: "user" | "optimal";
	skipPass?: number;
	pathResult?: {
		path: LineString;
		estimatedCoverage?: Polygon | MultiPolygon;
		validDepth: number;
		pathPoints: SimplePoint[];
		preMissionMetrics: PreMissionMetrics;
		clothoid: ParametricPath;
		turningRadiusMeters: number;
		planningParameters?: Record<string, unknown>;
		mitigationData?: {
			correctedTurns: SimplePoint[][];
			shortenedSegments: {
				Start: SimplePoint;
				End: SimplePoint;
			}[];
		};
	};
	pathLengthOptimal: boolean;
	workAreaAreaOptimal: boolean;
	totalTimeOptimal: boolean;
	headlandDepthOptimal: boolean;
	turnTimeOptimal?: boolean;
	efficiencyOptimal: boolean;
	requestId: string;
	version: string;
};

export type PathSortingOption = "efficiency" | "area" | "time";

export const HEADLANDS_MAX_DEPTH = 2;
export const HEADLANDS_MIN_DEPTH = 1;
export const UNCAP_HEADLANDS_MAX_DEPTH = 8;
export const UNCAP_HEADLANDS_MIN_DEPTH = 2;
export const PASS_ORDER_VARIANTS = 6;
